/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, {useRef} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from 'tss-react/mui';
import {useTheme} from '@mui/material/styles';
import {useIntl} from 'react-intl';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import AddIcon from '@mui/icons-material/Add';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import useMediaQuery from '@mui/material/useMediaQuery';
import {Breakpoints as breakpoints} from 'wobi-web-common';
import {useDocumentCollection} from '../context/DocumentCollectionContext';
import {validateFileType, validateFileSize} from '../utils/documentHelpers';

const useStyles = makeStyles()((theme) => ({
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 20,
    padding: '8px 20px',
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
    fontFamily: 'Ploni, Arial, sans-serif',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: `${theme.palette.primary.main}14`,
    },
  },
  textButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    background: 'none',
    border: 'none',
    color: theme.palette.primary.main,
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 600,
    fontFamily: 'Ploni, Arial, sans-serif',
    cursor: 'pointer',
    padding: 0,
    textDecoration: 'underline',
    '&:hover': {
      opacity: 0.8,
    },
  },
  hiddenInput: {
    display: 'none',
  },
  bottomSheet: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.palette.background.paper,
    borderRadius: '16px 16px 0 0',
    boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
    zIndex: 1000,
    padding: '24px 20px',
    animation: 'slideUp 0.3s ease',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    zIndex: 999,
    animation: 'fadeIn 0.2s ease',
  },
  sheetTitle: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 700,
    fontFamily: 'Ploni, Arial, sans-serif',
    textAlign: 'center',
    marginBottom: 20,
    color: theme.palette.text.primary,
  },
  sheetOptions: {
    display: 'flex',
    justifyContent: 'center',
    gap: 40,
    marginBottom: 16,
  },
  sheetOption: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    padding: 12,
  },
  sheetIconWrapper: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px dashed #ccc',
  },
  sheetIconCamera: {
    backgroundColor: `${theme.palette.primary.main}1a`,
    borderColor: theme.palette.primary.main,
  },
  sheetIconGallery: {
    backgroundColor: `${theme.palette.secondary.main}1a`,
    borderColor: theme.palette.secondary.main,
  },
  sheetIcon: {
    fontSize: theme.typography.pxToRem(28),
  },
  sheetLabel: {
    fontSize: theme.typography.pxToRem(14),
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.primary,
  },
  cancelButton: {
    width: '100%',
    padding: '14px',
    borderRadius: 25,
    border: 'none',
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 600,
    fontFamily: 'Ploni, Arial, sans-serif',
    cursor: 'pointer',
    marginTop: 8,
  },
  '@global': {
    '@keyframes slideUp': {
      from: {transform: 'translateY(100%)'},
      to: {transform: 'translateY(0)'},
    },
    '@keyframes fadeIn': {
      from: {opacity: 0},
      to: {opacity: 1},
    },
  },
}));

const DEFAULT_ACCEPTED_FORMATS = 'image/jpeg,image/png,application/pdf,image/heic,image/heif';
const DEFAULT_MAX_FILE_SIZE_MB = 10;

const FileUploadButton = ({onFileSelected, label, variant = 'outlined'}) => {
  const {classes} = useStyles();
  const theme = useTheme();
  const intl = useIntl();
  const {uploadConfig} = useDocumentCollection();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [showSheet, setShowSheet] = React.useState(false);
  const [validationError, setValidationError] = React.useState('');
  const isMobile = useMediaQuery(`(max-width:${breakpoints.mobile}px)`);

  const allowedMimeTypes = uploadConfig?.allowedMimeTypes || DEFAULT_ACCEPTED_FORMATS.split(',');
  const acceptedFormats = allowedMimeTypes.join(',');
  const maxFileSizeBytes = (uploadConfig?.maxFileSizeMb || DEFAULT_MAX_FILE_SIZE_MB) * 1024 * 1024;

  const displayLabel = label || intl.formatMessage({id: 'docCollection.fileUpload.label'});

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const typeError = validateFileType(file, allowedMimeTypes);
      if (typeError) {
        const formats = allowedMimeTypes.map((t) => t.split('/')[1]?.toUpperCase()).filter(Boolean).join(', ');
        setValidationError(intl.formatMessage({id: 'docCollection.fileUpload.unsupportedType'}, {formats}));
        setShowSheet(false);
        e.target.value = '';
        return;
      }
      const sizeError = validateFileSize(file, maxFileSizeBytes);
      if (sizeError === 'emptyFile') {
        setValidationError(intl.formatMessage({id: 'docCollection.fileUpload.emptyFile'}));
        setShowSheet(false);
        e.target.value = '';
        return;
      }
      if (sizeError) {
        const maxMb = uploadConfig?.maxFileSizeMb || DEFAULT_MAX_FILE_SIZE_MB;
        setValidationError(intl.formatMessage({id: 'docCollection.fileUpload.fileTooLarge'}, {maxMb}));
        setShowSheet(false);
        e.target.value = '';
        return;
      }
      setValidationError('');
      onFileSelected(file);
      setShowSheet(false);
    }
    e.target.value = '';
  };

  const handleClick = () => {
    if (isMobile) {
      setShowSheet(true);
    } else {
      fileInputRef.current?.click();
    }
  };

  return (
    <>
      <button type='button' className={variant === 'text' ? classes.textButton : classes.button} onClick={handleClick}>
        {variant === 'text' && <AddIcon style={{fontSize: 18}} />}
        {displayLabel}
      </button>

      <Snackbar
        open={!!validationError}
        autoHideDuration={4000}
        onClose={() => setValidationError('')}
        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
      >
        <Alert severity='error' onClose={() => setValidationError('')} sx={{fontFamily: 'Ploni, Arial, sans-serif'}}>
          {validationError}
        </Alert>
      </Snackbar>

      <input
        ref={fileInputRef}
        type='file'
        accept={acceptedFormats}
        className={classes.hiddenInput}
        onChange={handleFileChange}
      />

      <input
        ref={cameraInputRef}
        type='file'
        accept='image/*'
        capture='environment'
        className={classes.hiddenInput}
        onChange={handleFileChange}
      />

      {showSheet && (
        <>
          <div className={classes.overlay} onClick={() => setShowSheet(false)} />
          <div className={classes.bottomSheet}>
            <div className={classes.sheetTitle}>{intl.formatMessage({id: 'docCollection.fileUpload.sheetTitle'})}</div>
            <div className={classes.sheetOptions}>
              <button
                type='button'
                className={classes.sheetOption}
                onClick={() => cameraInputRef.current?.click()}
              >
                <div className={`${classes.sheetIconWrapper} ${classes.sheetIconCamera}`}>
                  <CameraAltOutlinedIcon className={classes.sheetIcon} style={{color: theme.palette.primary.main}} />
                </div>
                <span className={classes.sheetLabel}>{intl.formatMessage({id: 'docCollection.fileUpload.camera'})}</span>
              </button>
              <button
                type='button'
                className={classes.sheetOption}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className={`${classes.sheetIconWrapper} ${classes.sheetIconGallery}`}>
                  <CollectionsOutlinedIcon className={classes.sheetIcon} style={{color: theme.palette.secondary.main}} />
                </div>
                <span className={classes.sheetLabel}>{intl.formatMessage({id: 'docCollection.fileUpload.gallery'})}</span>
              </button>
            </div>
            <button
              type='button'
              className={classes.cancelButton}
              onClick={() => setShowSheet(false)}
            >
              {intl.formatMessage({id: 'docCollection.fileUpload.cancel'})}
            </button>
          </div>
        </>
      )}
    </>
  );
};

FileUploadButton.propTypes = {
  label: PropTypes.string,
  onFileSelected: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['outlined', 'text']),
};

export default FileUploadButton;
